import type { ZoneAreaType } from "@produce/shared";
import { zoneServerErrorMessage, zoneShapeMessage } from "./zoneWriteError";

/**
 * Save the zones of one mapbox-gl-draw `draw.update`: a vertex edit, or a
 * move.
 *
 * A move carries EVERY selected feature — Draw's simple_select fires one
 * update with `getSelected()` after a shift-click or box-select drag. The
 * editor used to save only `features[0]`; the other moved zones stayed on
 * the map at a spot that was never saved. Now each one is judged and
 * written on its own:
 *
 *   • a shape validateZonePolygon refuses is never sent (the server's
 *     polygon trigger would refuse it too) → a refusal message;
 *   • a write the server refuses with SQLSTATE 22023 → a refusal message;
 *   • any other write error → `errors`, for the caller to log.
 *
 * zoneShapeUpdateFeedback then says what the caller toasts, and that it
 * re-syncs the map from the database whenever anything was saved, refused
 * or failed — so the map never shows a shape that was not saved. The write
 * itself is injected: this module knows no Supabase client, no map and no
 * toast.
 */

/** Writes one zone's new geometry; resolves with the error, or null when saved. */
export type ZoneShapeWriter = (id: string, geometry: GeoJSON.Geometry) => Promise<unknown>;

export type ZoneShapeUpdateResult = {
  /** Zones whose new shape is stored. */
  saved: number;
  /** One toast per refused zone (client check or server trigger). */
  refusals: string[];
  /** Write errors that are not a shape refusal (RLS, network, …). */
  errors: unknown[];
};

export async function saveZoneShapeUpdates(
  features: readonly GeoJSON.Feature[],
  write: ZoneShapeWriter,
): Promise<ZoneShapeUpdateResult> {
  const result: ZoneShapeUpdateResult = { saved: 0, refusals: [], errors: [] };

  for (const feature of features) {
    // Only saved zones carry a db id; a shape still being drawn is
    // draw.create's business.
    const dbId = feature.properties?.db_id ? String(feature.properties.db_id) : undefined;
    if (!dbId) continue;
    const areaType: ZoneAreaType = feature.properties?.area_type;

    const invalid = zoneShapeMessage(feature.geometry, areaType);
    if (invalid !== null) {
      result.refusals.push(invalid);
      continue;
    }

    // A writer that throws instead of returning its error (a dropped
    // connection) is handled the same, and the other zones still get saved.
    const error = await write(dbId, feature.geometry).catch((thrown: unknown) => thrown);
    if (error === null || error === undefined) {
      result.saved += 1;
      continue;
    }
    const refused = zoneServerErrorMessage(error, areaType);
    if (refused !== null) result.refusals.push(refused);
    else result.errors.push(error);
  }

  return result;
}

/** Shown once when a zone could not be written for a reason other than its shape. */
export const ZONE_SHAPE_SAVE_FAILED = "Zone not saved — please try again.";

export type ZoneShapeUpdateFeedback = {
  /** The error toasts, each once. */
  toasts: string[];
  /** Re-read the zones: the map may show a shape the database does not hold. */
  resync: boolean;
};

/** What the editor shows after saveZoneShapeUpdates, and whether it re-syncs the map. */
export function zoneShapeUpdateFeedback(result: ZoneShapeUpdateResult): ZoneShapeUpdateFeedback {
  const toasts = [...new Set(result.refusals)];
  if (result.errors.length > 0) toasts.push(ZONE_SHAPE_SAVE_FAILED);
  return { toasts, resync: result.saved > 0 || toasts.length > 0 };
}
